import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
    Drawer,
    useTheme,
    useMediaQuery,
    AppBar,
    Toolbar,
    Hidden,
    Fab,
    Zoom,
    Tooltip
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ClassIcon from '@mui/icons-material/Class';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';

import ManageStudentProgress from '../components/ManageStudentProgress';
import ManageStudents from '../components/ManageStudents/ManageStudents';
import ManageSubjectsUnits from '../components/SubjectsAndUnits/ManageSubjectsUnits';
import UserProfilePage from '../Pages/UserProfilePage';
import useTokenStore from '../components/hooks/useTokenStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TeacherDashboardLanding from './TeacherDashboardLanding';
import StudentAnalytics from '../components/ManageStudents/StudentDashboard/StudentAnalytics';
import TutorialDashboard from '../components/tutorials/TutorialDashboard';
import InteractiveGettingStartedGuide from '../components/tutorials/InteractiveGettingStartedGuide';

import TeacherResources from '../components/TeacherResources/TeacherResources';
import Upgrade from './Pricing/Upgrade';

import gradelyLogo from '../assets/logo/gradely-logo-1.png';
import { FeedbackTicketHub } from '../components/user_feedback_issues/';

// ---- TeacherDashBoardTemplate visual helpers (visuals only) ----
const getDrawerWidth = (isTablet, isMobile, isCollapsed) => {
    if (isMobile) return '100%';
    if (isTablet) return 320;
    return isCollapsed ? 100 : 300;
};

const navItems = [
    { key: 'Getting Started', label: 'Tutorial', icon: <PeopleAltIcon />, shortLabel: 'Start' },
    { key: 'Main DashBoard', label: 'Dashboard', icon: <PeopleAltIcon />, shortLabel: 'Dashboard' },
    { key: 'Manage Students', label: 'Students', icon: <PeopleAltIcon />, shortLabel: 'Students' },
    { key: 'Manage Subjects & Units', label: 'Subjects', icon: <ClassIcon />, shortLabel: 'Subjects' },
    { key: 'Manage Student Progress', label: 'Assessment', icon: <SchoolIcon />, shortLabel: 'Reports' },
    { key: 'TeacherResources', label: 'Resources', icon: <PeopleAltIcon />, shortLabel: 'Resources' },
    { key: 'Student Analytics', label: 'Analytics', icon: <PeopleAltIcon />, shortLabel: 'Analytics' },
    { key: 'Support', label: 'Support', icon: <PeopleAltIcon />, shortLabel: 'Support' },
    { key: 'Upgrade', label: 'Subscription', icon: <StarIcon />, shortLabel: 'Upgrade' },
];

const TeacherDashboard = ({ onLogout, user }) => {
    // ---- keep your logic/state unchanged ----
    const [activeTab, setActiveTab] = useState('Getting Started');
    const [searchParams, setSearchParams] = useSearchParams();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [activeTutorial, setActiveTutorial] = useState(null);

    const navigate = useNavigate();
    const { balance, fetchTokenBalance } = useTokenStore();
    const theme = useTheme();

    // Breakpoints (as in TeacherDashBoardTemplate)
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600–900
    const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 900
    const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900–1200

    const ALL_TABS = React.useMemo(
        () => new Set([
            ...navItems.map(i => i.key),
            'Profile', 'Feedback'
        ]),
        []
    );

    const selectTab = React.useCallback((key) => {
        setActiveTab(key);
        setSearchParams({ tab: key }, { replace: true });
        if (isMobile || isTablet) setMobileOpen(false);
    }, [setSearchParams, isMobile, isTablet]);

    useEffect(() => {
        const urlTab = searchParams.get('tab');
        if (urlTab && ALL_TABS.has(urlTab) && urlTab !== activeTab) {
            setActiveTab(urlTab);
        }
    }, [searchParams, ALL_TABS, activeTab]);

    useEffect(() => {
        fetchTokenBalance();
    }, [activeTab, fetchTokenBalance]);

    useEffect(() => {
        const onResize = () => setWindowHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const needsScrolling = isLaptop && windowHeight <= 700;

    const toggleSidebar = () => setSidebarCollapsed(s => !s);

    // Tutorial handlers
    const handleTutorialStart = (tutorialId) => {
        setActiveTutorial(tutorialId);
    };

    const handleTutorialClose = () => {
        setActiveTutorial(null);
    };


    // ---- DrawerContent visuals copied from TeacherDashBoardTemplate (only styles/markup) ----
    const DrawerContent = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 1.5, sm: 2, md: 2.5 },
            width: '100%',
            bgcolor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            position: 'relative',
            overflow: 'hidden',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                border: '2px solid transparent',
                backgroundClip: 'content-box',
                '&:hover': {
                    background: 'rgba(255, 255, 255, 0.5)',
                    backgroundClip: 'content-box',
                },
            },
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mb: { xs: 2, sm: 3 },
                p: { xs: 1, sm: 0 },
                minHeight: { xs: 48, sm: 56 },
                flexShrink: 0,
            }}>
                {/* Brand Icon */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 1, sm: 1.5 },
                    mt: (isMobile || isTablet) ? { xs: 8, sm: 8 } : { md: 0 },
                }}>
                    <Box
                        component="img"
                        src={gradelyLogo}
                        alt="Gradely Logo"
                        sx={{
                            width: sidebarCollapsed && isDesktop ? { xs: 60, sm: 65, md: 70 } : { xs: 80, sm: 100, md: 120 },
                            height: 'auto',
                            objectFit: 'contain',
                            filter: 'brightness(0) invert(1)',
                        }}
                    />
                </Box>

                {/* Welcome + Collapse Toggle */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    position: 'relative'
                }}>
                    <Typography
                        variant={isMobile ? 'h6' : 'subtitle1'}
                        fontWeight="bold"
                        textAlign="left"
                        sx={{
                            flex: 1,
                            color: '#ffffff',
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            display: sidebarCollapsed && isDesktop ? 'none' : 'block'
                        }}
                    >
                        {user?.display_name ? `Welcome, ${user.display_name}` : 'Welcome, Teacher'}
                    </Typography>

                    {isDesktop && (
                        <Tooltip title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'} placement="right">
                            <IconButton
                                onClick={toggleSidebar}
                                sx={{
                                    color: '#ffffff',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                    ml: 1,
                                    flexShrink: 0,
                                    p: 0.5,
                                }}
                            >
                                {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <Divider sx={{
                mb: { xs: 2, sm: 3 },
                borderColor: 'rgba(255,255,255,0.3)',
                borderWidth: '1px',
                display: sidebarCollapsed && isDesktop ? 'none' : 'block',
                flexShrink: 0,
            }} />

            {/* Scrollable nav area */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': {
                    background: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '3px',
                    '&:hover': { background: 'rgba(255, 255, 255, 0.3)' },
                },
            }}>
                <List sx={{
                    px: { xs: 0.5, sm: 1 },
                    '& .MuiListItem-root': { mb: { xs: 0.5, sm: 1 } },
                    flexShrink: 0,
                }}>
                    {navItems.map((item) => {
                        const button = (
                            <ListItemButton
                                onClick={() => selectTab(item.key)}
                                sx={{
                                    backgroundColor: activeTab === item.key ? 'rgba(255,255,255,0.15)' : 'transparent',
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    mb: { xs: 0.5, sm: 1 },
                                    border: activeTab === item.key ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                                    '&:hover': {
                                        backgroundColor: activeTab === item.key ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
                                        transform: 'translateX(4px)',
                                        transition: 'all 0.2s ease-in-out'
                                    },
                                    '&:active': { transform: 'translateX(2px) scale(0.98)' },
                                    px: { xs: 1.5, sm: 2, md: sidebarCollapsed && isDesktop ? 1 : 2.5 },
                                    py: { xs: 1.5, sm: 1.75, md: 2 },
                                    transition: 'all 0.2s ease-in-out',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    justifyContent: sidebarCollapsed && isDesktop ? 'center' : 'flex-start',
                                    '&::before': activeTab === item.key ? {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: '4px',
                                        bgcolor: '#ffffff',
                                        borderRadius: '0 2px 2px 0'
                                    } : {}
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        color: activeTab === item.key ? '#ffffff' : 'rgba(255,255,255,0.8)',
                                        minWidth: { xs: 36, sm: 40, md: sidebarCollapsed && isDesktop ? 32 : 44 },
                                        mr: sidebarCollapsed && isDesktop ? 0 : { xs: 1, sm: 1.5 },
                                        transition: 'color 0.2s ease-in-out'
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={sidebarCollapsed && isDesktop ? '' : (isMobile ? item.shortLabel : item.label)}
                                    primaryTypographyProps={{
                                        fontWeight: activeTab === item.key ? 'bold' : '600',
                                        color: activeTab === item.key ? '#ffffff' : 'rgba(255,255,255,0.9)',
                                        noWrap: false,
                                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                                        textShadow: activeTab === item.key ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                                        transition: 'all 0.2s ease-in-out',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                    }}
                                />
                            </ListItemButton>
                        );

                        return (
                            <ListItem key={item.key} disablePadding>
                                {sidebarCollapsed && isDesktop ? (
                                    <Tooltip title={item.label} placement="right" arrow>
                                        <Box component="span" sx={{ width: '100%' }}>
                                            {button}
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    button
                                )}
                            </ListItem>
                        );
                    })}




                </List>

                <Divider sx={{
                    mt: { xs: 2, sm: 3 },
                    mb: { xs: 2, sm: 3 },
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderWidth: '1px',
                    display: sidebarCollapsed && isDesktop ? 'none' : 'block',
                    flexShrink: 0,
                }} />

                {/* Bottom actions */}
                <Box sx={{
                    px: { xs: 0.5, sm: 1 },
                    pb: { xs: 1, sm: 2 },
                    display: 'block',
                    flexShrink: 0,
                }}>
                    {/* Profile */}
                    <ListItem disablePadding>
                        {sidebarCollapsed && isDesktop ? (
                            <Tooltip title="Profile" placement="right" arrow>
                                <Box component="span" sx={{ width: '100%' }}>
                                    <ListItemButton
                                        onClick={() => selectTab('Profile')}
                                        sx={{
                                            backgroundColor: activeTab === 'Profile' ? 'rgba(255,255,255,0.15)' : 'transparent',
                                            borderRadius: { xs: 1.5, sm: 2 },
                                            mb: { xs: 0.5, sm: 1 },
                                            border: activeTab === 'Profile' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                                            justifyContent: 'center',
                                            px: 1, py: { xs: 1.5, sm: 1.75, md: 2 },
                                            position: 'relative',
                                            '&::before': activeTab === 'Profile' ? {
                                                content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                                                bgcolor: '#ffffff', borderRadius: '0 2px 2px 0'
                                            } : {}
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: '#fff', minWidth: 32, mr: 0 }}>
                                            <AccountCircleIcon />
                                        </ListItemIcon>
                                        {/* hide text when collapsed */}
                                        <ListItemText primary="" />
                                    </ListItemButton>
                                </Box>
                            </Tooltip>
                        ) : (
                            <ListItemButton
                                onClick={() => selectTab('Profile')}
                                sx={{
                                    backgroundColor: activeTab === 'Profile' ? 'rgba(255,255,255,0.15)' : 'transparent',
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    mb: { xs: 0.5, sm: 1 },
                                    border: activeTab === 'Profile' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                                    px: { xs: 1.5, sm: 2, md: sidebarCollapsed && isDesktop ? 1 : 2.5 },
                                    py: { xs: 1.5, sm: 1.75, md: 2 },
                                    position: 'relative',
                                    '&::before': activeTab === 'Profile' ? {
                                        content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                                        bgcolor: '#ffffff', borderRadius: '0 2px 2px 0'
                                    } : {}
                                }}
                            >
                                <ListItemIcon sx={{ color: activeTab === 'Profile' ? '#fff' : 'rgba(255,255,255,0.8)' }}>
                                    <AccountCircleIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Profile"
                                    primaryTypographyProps={{
                                        fontWeight: activeTab === 'Profile' ? 'bold' : 600,
                                        color: activeTab === 'Profile' ? '#fff' : 'rgba(255,255,255,0.9)',
                                        noWrap: false, whiteSpace: 'normal', wordBreak: 'break-word'
                                    }}
                                />
                            </ListItemButton>
                        )}
                    </ListItem>

                    {/* Logout */}
                    <ListItem disablePadding>
                        {sidebarCollapsed && isDesktop ? (
                            <Tooltip title="Logout" placement="right" arrow>
                                <Box component="span" sx={{ width: '100%' }}>
                                    <ListItemButton
                                        onClick={onLogout}
                                        sx={{
                                            backgroundColor: 'rgba(244,67,54,0.8)',
                                            borderRadius: { xs: 1.5, sm: 2 },
                                            mt: { xs: 0.5, sm: 1 },
                                            justifyContent: 'center',
                                            px: 1, py: { xs: 1.5, sm: 1.75, md: 2 },
                                            border: '1px solid rgba(244,67,54,0.3)',
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: '#fff', minWidth: 32, mr: 0 }}>
                                            <LogoutIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="" />
                                    </ListItemButton>
                                </Box>
                            </Tooltip>
                        ) : (
                            <ListItemButton
                                onClick={onLogout}

                                sx={{
                                    backgroundColor: 'rgba(244,67,54,0.8)',
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    mt: { xs: 0.5, sm: 1 },
                                    border: '1px solid rgba(244,67,54,0.3)',
                                    px: { xs: 1.5, sm: 2, md: sidebarCollapsed && isDesktop ? 1 : 2.5 },
                                    py: { xs: 1.5, sm: 1.75, md: 2 },
                                }}
                            >
                                <ListItemIcon sx={{ color: '#fff' }}>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 'bold', color: '#fff' }} />
                            </ListItemButton>
                        )}
                    </ListItem>
                </Box>

            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            {/* Desktop Sidebar (TeacherDashBoardTemplate visuals) */}
            <Hidden mdDown>
                <Box
                    sx={{
                        width: getDrawerWidth(isTablet, isMobile, sidebarCollapsed),
                        flexBasis: getDrawerWidth(isTablet, isMobile, sidebarCollapsed),
                        flexShrink: 0,
                        bgcolor: theme.palette.primary.dark,
                        color: theme.palette.primary.contrastText,
                        borderRight: '1px solid #e0e0e0',
                        height: '100vh',
                        minHeight: '100vh',
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        zIndex: 1200,
                        transition: 'width 0.3s ease-in-out',
                    }}
                >
                    {DrawerContent}
                </Box>
            </Hidden>

            {/* Mobile/Tablet App Bar (TeacherDashBoardTemplate visuals) */}
            <Hidden mdUp>
                <AppBar
                    position="fixed"
                    sx={{
                        zIndex: 1300,
                        bgcolor: theme.palette.primary.dark,
                        boxShadow: 'none',
                        borderBottom: `1px solid ${theme.palette.primary.light}`
                    }}
                >
                    <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
                        <IconButton color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#fff' }}
                        >
                            {navItems.find(item => item.key === activeTab)?.label || 'Dashboard'}
                        </Typography>

                    </Toolbar>
                </AppBar>
            </Hidden>

            {/* Main Content */}
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                overflow: 'hidden',
                ml: { xs: 0, md: `${getDrawerWidth(isTablet, isMobile, sidebarCollapsed)}px` },
                pt: { xs: '56px', sm: '64px', md: 0 },
                transition: 'margin-left 0.3s ease-in-out',
            }}>
                <Box sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2, md: 3 },
                    overflowY: 'auto',
                    backgroundColor: theme.palette.background.default,
                    minHeight: 0
                }}>
                    {activeTab === 'Getting Started' && (
                        <TutorialDashboard
                            onTutorialStart={handleTutorialStart}
                            activeTutorial={activeTutorial}
                        />
                    )}
                    {activeTab === 'Main DashBoard' && <TeacherDashboardLanding setActiveTab={selectTab} setActiveTabGlobal={selectTab} user={user} />}
                    {activeTab === 'Manage Students' && <ManageStudents />}
                    {activeTab === 'Manage Subjects & Units' && <ManageSubjectsUnits setActiveTabGlobal={selectTab} />}
                    {activeTab === 'Manage Student Progress' && <ManageStudentProgress setActiveTabGlobal={selectTab} />}
                    {activeTab === 'TeacherResources' && <TeacherResources setActiveTabGlobal={selectTab} />}
                    {activeTab === 'Student Analytics' && <StudentAnalytics />}
                    {activeTab === 'Upgrade' && <Upgrade />}
                    {activeTab === 'Support' && <FeedbackTicketHub />}
                    {activeTab === 'Profile' && <UserProfilePage />}
                </Box>
            </Box>

            {/* Mobile/Tablet Drawer (TeacherDashBoardTemplate visuals) */}
            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        width: getDrawerWidth(isTablet, isMobile, false),
                        bgcolor: theme.palette.primary.dark,
                        color: theme.palette.primary.contrastText,
                        boxSizing: 'border-box',
                    },
                }}
            >
                {DrawerContent}
            </Drawer>

            {/* Floating Action Button for Mobile (TeacherDashBoardTemplate visuals) */}
            <Hidden mdUp>
                <Zoom in={!mobileOpen}>
                    <Fab
                        color="primary"
                        aria-label="menu"
                        onClick={() => setMobileOpen(true)}
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            zIndex: 1000,
                            bgcolor: theme.palette.primary.main,
                            '&:hover': { bgcolor: theme.palette.primary.dark },
                        }}
                    >
                        <MenuIcon />
                    </Fab>
                </Zoom>
            </Hidden>

            {/* Global Tutorial - Persists across tabs */}
            {activeTutorial === 'interactive-guide' && (
                <InteractiveGettingStartedGuide onComplete={handleTutorialClose} />
            )}

        </Box>
    );
};

export default TeacherDashboard;
